import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { DiagnosticsContext, type EngineSnapshot } from "@/ui/data/diagnosticsContext";
import type { OperationState } from "@/lib/ops/operationState";
import { getNetworkSnapshot } from "@/ui/data/networkHealth";
import { getHealthSnapshot } from "@/lib/observability/health";
import type { HealthSnapshot } from "@/lib/observability/health";
import { redactValue } from "@/lib/observability/redact";

const MAX_OPERATION_HISTORY = 120;
const MAX_ERROR_HISTORY = 80;
const BACKEND_ERROR_CODE_PATTERN = /\[([a-z0-9_]+)\]/i;

const readRuntimeProvider = (): "vm" | "supabase" => {
  const raw = (
    import.meta.env.VITE_MYTHIC_RUNTIME_PROVIDER
    ?? import.meta.env.NEXT_PUBLIC_MYTHIC_RUNTIME_PROVIDER
    ?? "vm"
  ).trim().toLowerCase();
  return raw === "supabase" ? "supabase" : "vm";
};

const readFunctionsBaseUrl = (): string | null => (
  import.meta.env.VITE_MYTHIC_FUNCTIONS_BASE_URL
  ?? import.meta.env.VITE_TAILSCALE_FUNCTIONS_BASE_URL
  ?? import.meta.env.NEXT_PUBLIC_MYTHIC_FUNCTIONS_BASE_URL
  ?? import.meta.env.NEXT_PUBLIC_TAILSCALE_FUNCTIONS_BASE_URL
  ?? null
);

const classifyFunctionsTarget = (baseUrl: string | null): "local" | "remote" | "unknown" => {
  if (!baseUrl) return "unknown";
  try {
    const hostname = new URL(baseUrl).hostname.toLowerCase();
    if (hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1") return "local";
    return "remote";
  } catch {
    return "unknown";
  }
};

const readLastBackendErrorCode = (
  lastError: string | null,
  errorHistory: Array<{ message: string; at: number }>,
): string | null => {
  const candidates = [
    lastError,
    ...errorHistory.map((entry) => entry.message),
  ];
  for (const message of candidates) {
    if (!message) continue;
    const match = BACKEND_ERROR_CODE_PATTERN.exec(message);
    if (match?.[1]) return match[1].toLowerCase();
  }
  return null;
};

const summarizeEdgeReachability = (checks: Record<string, HealthSnapshot>) => {
  const edgeChecks = Object.values(checks).filter((entry) => entry.subsystem.startsWith("edge:"));
  const total = edgeChecks.length;
  const ok = edgeChecks.filter((entry) => entry.status === "ok").length;
  const error = edgeChecks.filter((entry) => entry.status === "error").length;
  const unknown = edgeChecks.filter((entry) => entry.status === "unknown").length;
  return {
    total,
    ok,
    error,
    unknown,
    status: error > 0 ? "error" : total > 0 && unknown === total ? "unknown" : "ok",
  };
};

const summarizeSubsystem = (
  checks: Record<string, HealthSnapshot>,
  subsystem: string,
) => {
  const row = checks[subsystem];
  if (!row) {
    return {
      status: "unknown",
      last_success_at: null,
      last_error_at: null,
      last_error: null,
      last_latency_ms: null,
    };
  }
  return {
    status: row.status,
    last_success_at: row.last_success_at,
    last_error_at: row.last_error_at,
    last_error: row.last_error,
    last_latency_ms: row.last_latency_ms,
  };
};

export function DiagnosticsProvider({ children }: { children: ReactNode }) {
  const [lastError, setLastErrorState] = useState<string | null>(null);
  const [lastErrorAt, setLastErrorAt] = useState<number | null>(null);
  const [errorHistory, setErrorHistory] = useState<Array<{ message: string; at: number }>>([]);
  const [engineSnapshot, setEngineSnapshot] = useState<EngineSnapshot | null>(null);
  const [operations, setOperations] = useState<OperationState[]>([]);
  const [healthChecks, setHealthChecks] = useState<Record<string, HealthSnapshot>>(
    getHealthSnapshot(),
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setHealthChecks(getHealthSnapshot());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const setLastError = useCallback((message: string | null) => {
    const at = message ? Date.now() : null;
    setLastErrorState(message);
    setLastErrorAt(at);
    if (message && at) {
      setErrorHistory((prev) => [{ message, at }, ...prev].slice(0, MAX_ERROR_HISTORY));
    }
  }, []);

  const recordOperation = useCallback((operation: OperationState) => {
    setOperations((prev) => {
      const idx = prev.findIndex((entry) => entry.id === operation.id);
      if (idx === -1) {
        return [operation, ...prev].slice(0, MAX_OPERATION_HISTORY);
      }
      const next = [...prev];
      next[idx] = operation;
      return next;
    });
  }, []);

  const exportDebugBundle = useCallback(() => {
    const network = getNetworkSnapshot();
    const healthChecks = getHealthSnapshot();
    const runtimeProvider = readRuntimeProvider();
    const functionsBaseUrl = readFunctionsBaseUrl();
    const functionsTarget = classifyFunctionsTarget(functionsBaseUrl);
    const lastBackendProviderErrorCode = readLastBackendErrorCode(lastError, errorHistory);
    const edgeReachability = summarizeEdgeReachability(healthChecks);
    const narrativeSyncTelemetry = {
      sync_state: engineSnapshot?.syncState ?? "unknown",
      sync_reason: engineSnapshot?.syncReason ?? null,
      committed_turn_id: engineSnapshot?.lastCommittedTurnId ?? null,
      committed_turn_index: engineSnapshot?.lastCommittedTurnIndex ?? null,
      dm_turn_id: engineSnapshot?.lastDmTurnId ?? null,
      dm_turn_index: engineSnapshot?.lastDmTurnIndex ?? null,
      scene_cache_updated_at: engineSnapshot?.lastSceneCacheUpdate ?? null,
      last_action_chip_render_at: engineSnapshot?.lastActionChipRender ?? null,
      presentation_mode: engineSnapshot?.presentationMode ?? null,
    };
    const progressionTelemetry = {
      points_spend: summarizeSubsystem(healthChecks, "edge:mythic-spend-points"),
      reward_sync_dm: summarizeSubsystem(healthChecks, "edge:mythic-dungeon-master"),
      reward_sync_combat: summarizeSubsystem(healthChecks, "edge:mythic-combat-tick"),
      runtime_transition: summarizeSubsystem(healthChecks, "edge:mythic-runtime-transition"),
      character_refresh: summarizeSubsystem(healthChecks, "edge:mythic-recompute-character"),
      narrative_sync: narrativeSyncTelemetry,
      board_sync: narrativeSyncTelemetry,
      last_reward_turn_id: engineSnapshot?.lastRewardTurnId ?? null,
      last_reward_source: engineSnapshot?.lastRewardSource ?? null,
      last_reward_deltas: {
        xp: engineSnapshot?.lastRewardXp ?? null,
        coins: engineSnapshot?.lastRewardCoins ?? null,
        loot: engineSnapshot?.lastRewardLoot ?? [],
      },
      last_points_spend: {
        status: engineSnapshot?.lastPointsSpendStatus ?? null,
        message: engineSnapshot?.lastPointsSpendMessage ?? null,
        at: engineSnapshot?.lastPointsSpendAt ?? null,
      },
      director_loop: {
        director_mode: engineSnapshot?.directorMode ?? null,
        turn_owner: engineSnapshot?.turnOwner ?? null,
        last_auto_source: engineSnapshot?.lastAutoSource ?? null,
        player_idle_timeout_triggered: engineSnapshot?.playerIdleTimeoutTriggered ?? false,
        last_override_at: engineSnapshot?.lastOverrideAt ?? null,
        queue_generation: engineSnapshot?.queueGeneration ?? null,
        auto_continue_applied: engineSnapshot?.autoContinueApplied ?? false,
      },
    };
    const payload = redactValue({
      generated_at: new Date().toISOString(),
      app: {
        mode: import.meta.env.DEV ? "development" : "production",
        version: import.meta.env.VITE_APP_VERSION ?? "0.0.0",
        git_sha: import.meta.env.VITE_GIT_SHA ?? "unknown",
        build_time: import.meta.env.VITE_BUILD_TIME ?? null,
      },
      env: {
        supabase_url: import.meta.env.VITE_SUPABASE_URL ?? null,
        has_supabase_anon_key: Boolean(import.meta.env.VITE_SUPABASE_ANON_KEY),
        runtime_provider: runtimeProvider,
        functions_base_url: functionsBaseUrl,
        functions_target: functionsTarget,
      },
      runtime: {
        href: typeof window !== "undefined" ? window.location.href : null,
        functions_runtime_health: edgeReachability,
      },
      diagnostics: {
        last_error: lastError,
        last_backend_provider_error_code: lastBackendProviderErrorCode,
        last_error_at: lastErrorAt,
        error_history: errorHistory,
        progression_telemetry: progressionTelemetry,
        engine_snapshot: engineSnapshot,
        operations: operations.slice(0, 80),
        health_checks: getHealthSnapshot(),
        network,
      },
    });
    return JSON.stringify(payload, null, 2);
  }, [engineSnapshot, errorHistory, lastError, lastErrorAt, operations]);

  const value = useMemo(() => ({
    lastError,
    lastErrorAt,
    setLastError,
    errorHistory,
    engineSnapshot,
    setEngineSnapshot,
    operations,
    recordOperation,
    healthChecks,
    exportDebugBundle,
  }), [
    errorHistory,
    engineSnapshot,
    exportDebugBundle,
    healthChecks,
    lastError,
    lastErrorAt,
    operations,
    recordOperation,
    setLastError,
    setEngineSnapshot,
  ]);

  return (
    <DiagnosticsContext.Provider value={value}>
      {children}
    </DiagnosticsContext.Provider>
  );
}
